import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { authApi, orderSciEventsApi } from 'api';
import { EventName, QueryKey } from 'enums';
import { authUtils, eventUtils } from 'utils';

export type UseOrderAuthResult = {
  orderId: string;
  authenticated: boolean;
  authenticationFailed: boolean;
  setAuthenticationFailed: (val: boolean) => void;
  isAuthLoading: boolean;
};

export const useOrderAuth = (): UseOrderAuthResult => {
  const params = useParams<{ id: string; token: string }>();
  const authData = useMemo(
    () => ({ orderId: params.id as string, token: params.token as string }),
    [params],
  );

  const [authenticated, setAuthenticated] = useState(false);
  const [authenticationFailed, setAuthenticationFailed] = useState(false);

  const { isLoading: isAuthLoading } = useQuery(
    [QueryKey.Login, authData],
    () => authApi.login(authData),
    {
      enabled: !authenticated && !authenticationFailed,
      retry: false,
      onSuccess: (response: { accessToken: string }) => {
        authUtils.setAuthData(authData);
        authUtils.setAccessToken(response?.accessToken);
        setAuthenticated(true);
        eventUtils.flushPendingEvents();
      },
      onError: (error: AxiosError<{ message: string }>) => {
        setAuthenticationFailed(true);
        if (authData.orderId && authData.token) {
          orderSciEventsApi
            .createPublic(authData.orderId, authData.token, {
              event: EventName.LoginFailed,
              message: error.response
                ? `${error.response.status} ${error.response.data?.message}`
                : error.message,
            })
            .catch(() => {});
        }
      },
    },
  );

  return {
    orderId: authData.orderId,
    authenticated,
    authenticationFailed,
    setAuthenticationFailed,
    isAuthLoading,
  };
};
